import { EventConfig, PlaybackEvent, PlayerEvent, SeekEvent } from 'bitmovin-player';
import { useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { playerTypes } from '@libs/constants';
import cdnHandler from '../../../helpers/CDNHandler';
import playerService from '../../../helpers/bitmovinPlayer';
import { safePlayerPlay } from '../../../helpers/playerGuards';
import f from '../../../helpers/formatter2';
import WaterMark from '../../../helpers/watermark';
import { getItemProperties } from '../../../contexts/MixpanelHelper';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { __ } from '../../../translations';
// import { HandleError, ProgramData } from '.';
import { toast } from '@intigral/shared';
import { vodData, ProgramData } from '../types/IPlayer';
import { avodMixpanelEvents } from '../../../helpers/avodHandler';
import { Genesys } from '../../../components/ui/Genesys';
import adEventsHandlers from './adEventsHandlers';
import { playerSSAIManager } from '../../../utils/SSAI';

type UsePlayerEventsType = (
    selectedVideo: ProgramData | vodData | null,
    handlePlayerOnError: (e) => void,
    handlePlayerOnFinish?: () => void,
    handlePlayerOnDestroy?: () => void,
    handlePlayerOnSeeked?: () => void,
    handleOnStallEnded?: () => void,
    onSourceLoaded?: () => void,
    handleOnAudioTrackChange?: (e) => void,
    handleFirstPlay?: () => void,
    handlePlayerOnAdSeeked?: (e) => void,
    handlePlayerTimeChanged?: (e) => void,
    sendBookmarksOnPause?: () => void,
    sendBookmarksOnSeek?: (seekTarget: number) => void,
    stopOwnCDNSession?: (reason?: string) => void
) => EventConfig;

/**
 * Analytics event handlers for the player.
 */
export const usePlayerEvents: UsePlayerEventsType = (
    selectedVideo,
    handlePlayerOnError,
    handlePlayerOnFinish,
    handlePlayerOnDestroy,
    handlePlayerOnSeeked,
    handleOnStallEnded,
    onSourceLoaded,
    handleOnAudioTrackChange,
    handleFirstPlay,
    handlePlayerOnAdSeeked,
    onTimeChanged,
    sendBookmarksOnPause,
    sendBookmarksOnSeek,
    stopOwnCDNSession
) => {
    const { track } = useContext(MixpanelContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const playbackTimestamp = searchParams?.get('playbackTimestamp') ?? selectedVideo?.playbackTimestamp ?? null;
    return {
        [PlayerEvent.StallStarted]: (event) => {
            track(EVENT.CATCHUP_PLAYER.BUFFERING, {
                ...getItemProperties(selectedVideo),
                Playtime: playerService.getPlayer().getCurrentTime(),
            });
        },
        [PlayerEvent.SourceLoaded]: (event) => {
            // Clear ads data when source is loaded to avoid ads logic from old playback to be executed
            playerService.clearAdsData();

            if (onSourceLoaded) {
                onSourceLoaded();
            }
        },
        [PlayerEvent.Play]: (event: PlaybackEvent) => {
            track(EVENT.CATCHUP_PLAYER.PLAY_CLICK, {
                ...getItemProperties(selectedVideo),
            });
            playerSSAIManager.handlePlayerResumed();
            avodMixpanelEvents.onAdResumed(event);
        },
        [PlayerEvent.Playing]: (event) => {
            track(EVENT.CATCHUP_PLAYER.START, {
                ...getItemProperties(selectedVideo),
                'VOD Duration': f.converToHHMMSS(playerService.getPlayer()?.getDuration()),
                'Video Type': playerTypes.Vod,
            });
            // if (handleFirstPlay) {
            //     handleFirstPlay();
            // }
        },
        [PlayerEvent.Error]: (event: any) => {
            track(EVENT.CATCHUP_PLAYER.ERROR_POPUP, {
                ...getItemProperties(selectedVideo),
                'Error Code': event,
            });
            handlePlayerOnError(event);
            // Only this player's session: an error in an old player must not stop a newer one's.
            stopOwnCDNSession?.('player error');
        },
        [PlayerEvent.Paused]: (event: PlaybackEvent) => {
            //showErrorInUI('Custom Error Message');
            track(EVENT.CATCHUP_PLAYER.PAUSE_CLICK, {
                ...getItemProperties(selectedVideo),
            });
            playerSSAIManager.handlePlayerPaused();
            avodMixpanelEvents.onAdPasued(event);
            if (sendBookmarksOnPause && event?.issuer !== 'ui-seek') {
                sendBookmarksOnPause();
            }
        },
        [PlayerEvent.Seeked]: (event) => {
            //showErrorInUI('Custom Error Message');
            track(EVENT.CATCHUP_PLAYER.SEEKED, {
                ...getItemProperties(selectedVideo),
            });
            if (handlePlayerOnSeeked) {
                handlePlayerOnSeeked();
            }
        },
        [PlayerEvent.PlaybackFinished]: (event) => {
            track(EVENT.CATCHUP_PLAYER.COMPLETED, {
                Playtime: f.converToHHMMSS(playerService.getPlayer()?.getDuration()),
                ...getItemProperties(selectedVideo),
            });
            //showErrorInUI('Custom Error Message');
            if (handlePlayerOnFinish) {
                handlePlayerOnFinish();
            }
            stopOwnCDNSession?.('playback finished');
            // navigate(-1);
        },
        [PlayerEvent.Ready]: (event) => {
            setTimeout(() => {
                if (handleFirstPlay) {
                    handleFirstPlay();
                }
            }, 200);
            if (!playerService.getPlayer() || !playbackTimestamp) {
                return;
            }
            const duration = playerService.getPlayer()?.getDuration();
            const timestamp = Number(playbackTimestamp);
            if (isNaN(timestamp) || timestamp < 0 || timestamp > duration) {
                toast(__('player_err_invalid_timestamp'), 'error');
                playerService.getPlayer().seek(0);
                return;
            }
            if (isFinite(duration)) {
                playerService.getPlayer().seek(timestamp < duration ? timestamp : 0);
            }
            safePlayerPlay(playerService.getPlayer());
        },
        [PlayerEvent.Destroy]: (event) => {
            if (handlePlayerOnDestroy) {
                handlePlayerOnDestroy();
            }
            playerSSAIManager.handlePlayerDestroyed();
            avodMixpanelEvents.onAdExit();
            // Bitmovin fires Destroy *after* the player API is gone, so the session bound to this
            // player is already stopped by `destroyPlayer()`. Only stop here if the session is still
            // bound to a dead player, never one already re-bound to a newer (live) instance.
            cdnHandler.stopCDNSessionIfPlayerGone();
            Genesys.show();
        },
        [PlayerEvent.StallEnded]: (event) => {
            if (handleOnStallEnded) {
                handleOnStallEnded();
            }
        },
        [PlayerEvent.AudioChanged]: (event) => {
            if (handleOnAudioTrackChange) {
                handleOnAudioTrackChange(event);
            }
        },

        [PlayerEvent.TimeChanged]: (event: PlaybackEvent) => {
            if (onTimeChanged) {
                onTimeChanged(event);
            }
            if (adEventsHandlers.onTimeChanged) {
                adEventsHandlers.onTimeChanged();
            }
        },
        [PlayerEvent.Seek]: (event: SeekEvent) => {
            if (handlePlayerOnAdSeeked) {
                handlePlayerOnAdSeeked(event);
            }
            // Seekbar seeks are issued with 'ui'
            if (sendBookmarksOnSeek && event?.issuer === 'ui') {
                sendBookmarksOnSeek(event.seekTarget);
            }
            playerSSAIManager.handlePlayerSeek(event as any);
        },

        ...adEventsHandlers,
    };
};
